import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import TwoFactorSection from "@/components/dashboard/TwoFactorSection";
import SmsPreferences from "@/components/dashboard/SmsPreferences";
import { PublicActivityCard } from "@/components/dashboard/PublicActivityCard";

/**
 * Self-serve account settings — usable by any signed-in role.
 * Profile: name + company (PATCH /api/auth/me)
 * Security: change password (POST /api/auth/change-password)
 */
export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", company: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState({ profile: false, password: false });
  const [pwErr, setPwErr] = useState(null);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", company: user.company || "" });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy((b) => ({ ...b, profile: true }));
    try {
      const { data } = await api.patch("/auth/me", { name: form.name, company: form.company });
      setUser?.(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally {
      setBusy((b) => ({ ...b, profile: false }));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr(null);
    if (pw.new_password.length < 8) return setPwErr("New password must be at least 8 characters.");
    if (pw.new_password !== pw.confirm) return setPwErr("New passwords do not match.");
    if (pw.current_password === pw.new_password) return setPwErr("New password must be different from current.");
    setBusy((b) => ({ ...b, password: true }));
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPwErr(typeof detail === "string" ? detail : "Could not change password.");
    } finally {
      setBusy((b) => ({ ...b, password: false }));
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <SectionTitle title="Account settings" subtitle="Manage your profile and security." />

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-grid place-items-center size-9 rounded-md bg-slate-900 text-white"><User className="size-4" strokeWidth={1.7} /></span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 leading-none">Profile</h2>
              <p className="text-xs text-slate-500 mt-1">Visible on invoices, payouts, and the public partner page.</p>
            </div>
          </div>
          <form onSubmit={saveProfile} className="grid sm:grid-cols-2 gap-4 max-w-xl" data-testid="profile-form">
            <div>
              <Label htmlFor="profile-name">Full name</Label>
              <Input id="profile-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
            </div>
            <div>
              <Label htmlFor="profile-company">Company</Label>
              <Input id="profile-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="profile-company" />
            </div>
            <div className="sm:col-span-2">
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled className="bg-slate-50" data-testid="profile-email" />
              <p className="text-[11px] text-slate-500 mt-1.5">Email is locked — contact <a href="mailto:ops@connex.is" className="text-[#0055FF] hover:underline">ops@connex.is</a> if you need to change it.</p>
            </div>
            <div className="sm:col-span-2 flex items-center justify-end gap-3">
              <Button disabled={busy.profile} type="submit" className="rounded-full" data-testid="profile-save">
                {busy.profile ? "Saving…" : <>Save changes <CheckCircle2 className="size-4" /></>}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-grid place-items-center size-9 rounded-md bg-slate-900 text-white"><Lock className="size-4" strokeWidth={1.7} /></span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 leading-none">Password</h2>
              <p className="text-xs text-slate-500 mt-1">8+ characters. We recommend a mix of letters, numbers and a symbol.</p>
            </div>
          </div>
          <form onSubmit={changePassword} className="grid gap-4 max-w-xl" data-testid="password-form">
            {pwErr && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800 flex items-start gap-2" data-testid="password-error">
                <AlertCircle className="size-4 mt-0.5 shrink-0" /><span>{pwErr}</span>
              </div>
            )}
            <div>
              <Label htmlFor="current_password">Current password</Label>
              <Input id="current_password" type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_password">New password</Label>
                <Input id="new_password" type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm new</Label>
                <Input id="confirm" type="password" required minLength={8} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" />
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Button disabled={busy.password} type="submit" className="rounded-full" data-testid="pw-submit">
                {busy.password ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Public site activity mode — admin only */}
      {user?.role === "admin" && <PublicActivityCard />}

      {/* Two-factor authentication — self-serve for every role */}
      <TwoFactorSection />

      {/* SMS notifications */}
      <SmsPreferences />
    </div>
  );
}
